import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../onyx/Button';
import { Tag } from '../onyx/Tag';
import {
  formatDisplayName,
  formatDisplayPhone,
  isBlankInstruction,
} from '../../utils/display-name';
import {
  CLINIC_PROVIDERS,
  isActiveSampleCollection,
  isClinicSampleCollection,
  isPendingMobileCollection,
} from './SampleCollectionStatusSection';

const formatAnswer = (answer) => {
  if (Array.isArray(answer)) return answer.join(', ');
  if (typeof answer === 'boolean') return answer ? 'Yes' : 'No';
  if (answer === null || answer === undefined || answer === '') return '—';
  if (typeof answer === 'object') {
    const parts = [
      answer.address_1,
      answer.address_2,
      [answer.city, answer.state].filter(Boolean).join(', '),
      answer.zip_code,
    ].filter((part) => part && String(part).trim());
    if (parts.length) return parts.join(', ');
    if (answer.approximate_date) return String(answer.approximate_date);
    if (answer.date) return String(answer.date);
    try {
      return JSON.stringify(answer);
    } catch (_err) {
      return '—';
    }
  }
  return String(answer);
};

const formatDateNoTz = (value) => {
  if (!value) return null;
  const [y, m, d] = String(value).split('T')[0].split('-');
  if (!y || !m || !d) return String(value);
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  if (Number.isNaN(date.getTime())) return `${m}/${d}/${y}`;
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatAddress = (collection) => {
  if (!collection?.address_1) return null;
  const street = collection.address_2
    ? `${collection.address_1}, ${collection.address_2}`
    : collection.address_1;
  return `${street}, ${collection.city}, ${collection.state} ${collection.zip_code}`;
};

const contactLabel = (collection) => {
  if (collection?.preferred_contact_method === 'phone') return 'Phone call';
  if (collection?.preferred_contact_method === 'text') return 'Text message';
  if (collection?.preferred_contact_method) return 'Phone or text';
  return null;
};

const parseVisitParts = (collection) => {
  const formatted = collection?.collection_datetime_formatted;
  if (formatted?.date || formatted?.formatted) {
    const raw =
      typeof collection.collection_datetime === 'string'
        ? collection.collection_datetime
        : collection.collection_datetime?.iso;
    const date = raw ? new Date(raw) : null;
    if (date && !Number.isNaN(date.getTime())) {
      return {
        month: date.toLocaleString(undefined, { month: 'short' }),
        day: String(date.getDate()),
        dow: date.toLocaleString(undefined, { weekday: 'short' }),
        title: date.toLocaleString(undefined, {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
        }),
        sub:
          formatted.time
            ? `${formatted.time}${formatted.timezone ? ` ${formatted.timezone}` : ''}`
            : formatted.formatted || null,
      };
    }
  }
  return null;
};

const optionalFormCopy = (step) => {
  if (step.status === 'skipped') {
    return {
      badge: 'Optional',
      badgeTone: 'default',
      body: 'Informing us of upcoming health changes helps us plan follow-up visits.',
      cta: 'Fill out now',
    };
  }
  return {
    badge: 'Action needed',
    badgeTone: 'warn',
    body:
      step.description
      || 'Informing us of upcoming health changes helps us plan follow-up visits.',
    cta: 'Continue',
  };
};

const consentSignedLabel = (entry) => {
  if (entry?.completedAt) return `Signed ${formatDateNoTz(entry.completedAt)}`;
  const status = String(entry?.status || '').toLowerCase();
  if (['completed', 'signed', 'countersigned'].includes(status)) return 'Signed';
  if (['processing', 'pending', 'in_progress', 'sent'].includes(status)) {
    return 'Awaiting countersignature';
  }
  if (!entry?.status) return null;
  return entry.status.replace(/_/g, ' ');
};

const consentStatusTag = (entry) => {
  const status = String(entry?.status || '').toLowerCase();
  if (
    entry?.completedAt
    || ['completed', 'signed', 'countersigned'].includes(status)
  ) {
    return { label: 'Complete', variant: 'success' };
  }
  if (['processing', 'pending', 'in_progress', 'sent'].includes(status)) {
    return { label: 'Processing', variant: 'info' };
  }
  if (!entry?.status) return { label: 'Pending', variant: 'muted' };
  return { label: entry.status.replace(/_/g, ' '), variant: 'muted' };
};

const isConsentProcessing = (entry) => {
  const status = String(entry?.status || '').toLowerCase();
  return !entry?.completedAt
    && ['processing', 'pending', 'in_progress', 'sent'].includes(status);
};

const tagVariantForTone = (tone) => {
  if (tone === 'warn') return 'warning';
  if (tone === 'accent') return 'info';
  if (tone === 'ok') return 'success';
  return 'muted';
};

function TodoCard({ tasks }) {
  return (
    <section className="member-home-card" data-testid="member-home-todo">
      <div className="member-home-card__header">
        <h2 className="member-home-card__title">To do</h2>
        {tasks.length > 0 ? (
          <Tag variant="muted" density="compact">
            {tasks.length === 1 ? '1 item' : `${tasks.length} items`}
          </Tag>
        ) : null}
      </div>

      {tasks.length === 0 ? (
        <div className="member-home-empty">
          <div className="member-home-empty__mark" aria-hidden="true" />
          <div>
            <h3 className="member-home-empty__title">You&apos;re all caught up</h3>
            <p className="member-home-empty__body">
              We&apos;ll reach out when something needs your attention.
            </p>
          </div>
        </div>
      ) : (
        <ul className="member-home-todo-list">
          {tasks.map((task) => (
            <li key={task.id} className="member-home-task">
              <div
                className={[
                  'member-home-task__accent',
                  task.badgeTone === 'warn'
                    ? 'member-home-task__accent--warn'
                    : task.badgeTone === 'accent'
                      ? 'member-home-task__accent--accent'
                      : task.badgeTone === 'ok'
                        ? 'member-home-task__accent--ok'
                        : '',
                ].filter(Boolean).join(' ')}
                aria-hidden="true"
              />
              <div className="member-home-task__copy">
                <h3 className="member-home-task__title">{task.title}</h3>
                {task.body ? (
                  <p className="member-home-task__body">{task.body}</p>
                ) : null}
                <div className="member-home-task__meta">
                  {task.badge ? (
                    <Tag
                      variant={tagVariantForTone(task.badgeTone)}
                      density="compact"
                    >
                      {task.badge}
                    </Tag>
                  ) : null}
                  {task.meta ? (
                    <span className="member-home-task__meta-text">{task.meta}</span>
                  ) : null}
                </div>
              </div>
              {task.cta ? (
                <div className="member-home-task__cta">
                  {task.cta}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function StudyVisitCard({ collections, onStartPending }) {
  const pending = collections.find(isPendingMobileCollection);
  const active = collections.filter(isActiveSampleCollection);
  const collection = pending || active[0];

  if (!collection) {
    return null;
  }

  const clinic = isClinicSampleCollection(collection)
    ? CLINIC_PROVIDERS[collection.phlebotomy_provider]
    : null;
  const when = parseVisitParts(collection);
  const address = formatAddress(collection);
  const contact = contactLabel(collection);
  const phone = formatDisplayPhone(collection.phone_number);
  const notes = isBlankInstruction(collection.special_instructions)
    ? null
    : collection.special_instructions;

  let chip = { label: 'Scheduling', tone: 'accent' };
  let headline = 'Thank you for enrolling';
  let body = 'Thanks for providing your study visit preferences. We\'ll be in touch soon about next steps.';

  if (pending) {
    chip = { label: 'Action needed', tone: 'warn' };
    headline = 'Confirm your visit details';
    body = 'Review your contact info and address so we can schedule your study visit.';
  } else if (clinic && collection.collection_datetime) {
    chip = { label: 'Scheduled', tone: 'accent' };
    headline = null;
    body = `Your visit at ${clinic.clinicName || 'the clinic'} is confirmed. Please arrive on time and bring a photo ID.`;
  } else if (collection.status === 'info_provided') {
    chip = { label: 'Scheduling', tone: 'accent' };
    headline = 'Thank you for enrolling';
    body = 'Thanks for providing your study visit preferences. We\'ll be in touch soon about next steps.';
  } else if (['confirmed', 'selected', 'contacted', 'rescheduled'].includes(collection.status)) {
    chip = { label: 'Scheduled', tone: 'accent' };
    headline = when?.title || 'Your visit is scheduled';
    body = 'Arrive on time and bring a photo ID.';
  } else if (['completed', 'done'].includes(collection.status)) {
    chip = { label: 'Complete', tone: 'ok' };
    headline = when ? `Collected on ${when.title}` : 'Visit complete';
    body = 'Your sample is on its way to the lab. Nothing else is needed for this visit.';
  }

  return (
    <section className="member-home-card" data-testid="member-home-study-visit">
      <div className="member-home-card__header">
        <h2 className="member-home-card__title">Your study visit</h2>
        <Tag variant={tagVariantForTone(chip.tone)} density="compact">
          {chip.label}
        </Tag>
      </div>

      {headline || body ? (
        <div className="member-home-visit__copy">
          {headline ? <p className="member-home-visit__headline">{headline}</p> : null}
          {body ? <p className="member-home-visit__body">{body}</p> : null}
        </div>
      ) : null}

      {when && !pending ? (
        <div className="member-home-visit__when">
          <div className="member-home-visit__datebox" aria-hidden="true">
            <div className="member-home-visit__month">{when.month}</div>
            <div className="member-home-visit__day">{when.day}</div>
            <div className="member-home-visit__dow">{when.dow}</div>
          </div>
          <div className="member-home-visit__when-copy">
            <p className="member-home-visit__when-title">{when.title}</p>
            {when.sub ? <p className="member-home-visit__when-sub">{when.sub}</p> : null}
          </div>
        </div>
      ) : null}

      <dl className="member-home-dl">
        {address ? (
          <div className="member-home-dl__row">
            <dt>Visit location</dt>
            <dd>
              {clinic?.clinicName ? <strong>{clinic.clinicName}</strong> : null}
              {clinic?.clinicName ? <br /> : null}
              {address}
            </dd>
          </div>
        ) : null}
        {(contact || phone) ? (
          <div className="member-home-dl__row">
            <dt>Preferred contact</dt>
            <dd>
              {[contact, phone].filter(Boolean).join(' · ')}
            </dd>
          </div>
        ) : null}
        {notes ? (
          <div className="member-home-dl__row">
            <dt>Notes for phlebotomist</dt>
            <dd>{notes}</dd>
          </div>
        ) : null}
      </dl>

      {pending ? (
        <Button type="button" variant="primary" onClick={onStartPending}>
          Confirm details
        </Button>
      ) : null}
    </section>
  );
}

function AccordionItem({ id, label, meta, openId, setOpenId, children }) {
  const open = openId === id;
  return (
    <div className="member-home-accordion__item" data-open={open ? 'true' : 'false'}>
      <h3>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpenId(open ? null : id)}
        >
          <span className="member-home-accordion__trigger-main">
            <span className="member-home-accordion__label">{label}</span>
            {meta ? <span className="member-home-accordion__meta">{meta}</span> : null}
          </span>
          <span className="member-home-accordion__chevron" aria-hidden="true" />
        </button>
      </h3>
      <div className="member-home-accordion__panel" hidden={!open}>
        {children}
      </div>
    </div>
  );
}

function AnswerList({ data }) {
  if (!data || Object.keys(data).length === 0) {
    return <p className="member-home-visit__body">No answers on file yet.</p>;
  }
  return (
    <dl className="member-home-dl member-home-dl--answers">
      {Object.entries(data).map(([id, entry]) => (
        <div key={id} className="member-home-dl__row">
          <dt>{entry.question}</dt>
          <dd>{formatAnswer(entry.answer)}</dd>
        </div>
      ))}
    </dl>
  );
}

const CONSENT_STATUS_META_KEYS = new Set([
  'all_consents_completed',
  'required_documents',
  'document_meta',
  'flow_name',
  'questions_form_url',
]);

function consentEntries(consents) {
  if (!consents) return [];
  const labels = {};
  const meta = consents.document_meta;
  if (Array.isArray(meta)) {
    meta.forEach((doc) => {
      if (doc?.document_type && doc.label) labels[doc.document_type] = doc.label;
    });
  } else if (meta && typeof meta === 'object') {
    Object.entries(meta).forEach(([key, doc]) => {
      const label = doc?.label || doc?.title;
      if (label) labels[key] = label;
    });
  }
  return Object.entries(consents)
    .filter(
      ([key, entry]) =>
        !CONSENT_STATUS_META_KEYS.has(key)
        && entry
        && typeof entry === 'object'
        && !Array.isArray(entry)
        && ('status' in entry || 'completedAt' in entry),
    )
    .map(([docKey, entry]) => ({
      key: docKey,
      label: labels[docKey] || `${docKey.charAt(0).toUpperCase()}${docKey.slice(1)} Consent`,
      entry,
    }));
}

/**
 * Hero lede should set context — not repeat the To do CTA.
 * Portal connect is recommended/optional; study visit and required forms lead.
 */
function heroLede({ actionableCount, needsPortal, hasStudyVisit }) {
  if (actionableCount > 0) {
    if (actionableCount === 1) {
      return 'One item still needs your attention below.';
    }
    return 'A few things still need your attention below.';
  }
  if (hasStudyVisit) {
    return 'Your enrollment is complete. We\'ll keep you posted on study visit logistics.';
  }
  if (needsPortal) {
    return 'Your enrollment is complete. Connecting a health portal is optional and helps us pull your records.';
  }
  return 'Your enrollment is complete. We\'ll reach out when something needs your attention.';
}

/**
 * Enrolled member study-home — mock layout wired to real onboarding / visit / Fasten data.
 */
export function MemberHome({
  profileData,
  optionalSteps = [],
  stepStatuses = null,
  sampleCollections = [],
  onStartPending,
  updatesData,
  preScreenData,
  verificationData,
  consentData,
  onDownloadConsent,
  fastenEnabled,
  fastenConnections = [],
  fastenWidget,
}) {
  const resolvedOptionalSteps = (() => {
    const steps = Array.isArray(optionalSteps) ? [...optionalSteps] : [];
    const hasUpdates = steps.some((step) => step.key === 'UPDATES');
    if (!hasUpdates && stepStatuses?.UPDATES) {
      const status = stepStatuses.UPDATES.status === 'completed' ? 'completed' : 'pending';
      steps.push({
        key: 'UPDATES',
        label: 'Health Updates',
        path: '/updates',
        kind: 'onboarding_form',
        description:
          'Informing us of upcoming health changes helps us plan follow-up visits.',
        status,
        completed_at: stepStatuses.UPDATES.completed_at || null,
        can_open: true,
      });
    }
    return steps;
  })();

  const updatesStep = resolvedOptionalSteps.find((step) => step.key === 'UPDATES');
  const hasUpdatesAnswers =
    Boolean(updatesData) && Object.keys(updatesData).length > 0;
  const showUpdatesAccordion = Boolean(updatesStep && hasUpdatesAnswers);
  const [openId, setOpenId] = useState(null);

  const firstName = formatDisplayName(
    profileData?.first_name || verificationData?.first_name || '',
  );

  const actionableOptional = resolvedOptionalSteps.filter(
    (step) => step.status === 'pending' || step.status === 'skipped',
  );
  const otherCompletedOptional = resolvedOptionalSteps.filter(
    (step) => step.status === 'completed' && step.key !== 'UPDATES',
  );

  const needsPortal = Boolean(fastenEnabled && fastenConnections.length === 0);
  const tasks = [];

  actionableOptional.forEach((step) => {
    const copy = optionalFormCopy(step);
    tasks.push({
      id: `form-${step.key}`,
      title: step.label || step.key,
      body: copy.body,
      badge: copy.badge,
      badgeTone: copy.badgeTone,
      cta: step.can_open && step.path ? (
        <Button type="button" variant="primary" component={Link} to={step.path}>
          {copy.cta}
        </Button>
      ) : null,
    });
  });

  if (needsPortal) {
    tasks.push({
      id: 'fasten-connect',
      title: 'Connect your health portal',
      body: 'Optionally link MyChart or a similar portal so we can securely pull your medical records.',
      badge: 'Recommended',
      badgeTone: 'accent',
      cta: (
        <Button
          type="button"
          variant="primary"
          onClick={() => setOpenId('portals')}
        >
          Connect portal
        </Button>
      ),
    });
  }

  const consents = consentEntries(consentData);
  const processingConsents = consents.filter(({ entry }) => isConsentProcessing(entry));
  const consentMeta = (() => {
    if (!consents.length) return null;
    const count = `${consents.length} document${consents.length === 1 ? '' : 's'}`;
    if (processingConsents.length) {
      return `${count} · ${processingConsents.length} processing`;
    }
    return count;
  })();
  const hasStudyVisit =
    sampleCollections.some(isPendingMobileCollection)
    || sampleCollections.some(isActiveSampleCollection);

  const updatesMeta = (() => {
    if (!updatesStep || !hasUpdatesAnswers) return null;
    if (updatesStep.completed_at) {
      return `Updated ${formatDateNoTz(updatesStep.completed_at)}`;
    }
    return 'Completed';
  })();

  const verificationStreet = formatDisplayName(
    verificationData?.address_street || verificationData?.address_street_1 || '',
  );
  const verificationStreet2 = formatDisplayName(verificationData?.address_street_2 || '');
  const verificationCityStateZip = (() => {
    if (!verificationData) return '';
    const city = formatDisplayName(verificationData.address_city || '');
    const state = verificationData.address_state || '';
    const zip = verificationData.address_postal_code || '';
    const left = [city, state].filter(Boolean).join(', ');
    return [left, zip].filter(Boolean).join(' ');
  })();

  return (
    <div className="member-home" data-testid="member-home">
      <header className="member-home__hero">
        <p className="member-home__eyebrow">Your study home</p>
        <h1 className="member-home__title">
          {firstName ? `Hi ${firstName}` : 'Welcome back'}
        </h1>
        <p className="member-home__lede">
          {heroLede({
            actionableCount: actionableOptional.length,
            needsPortal,
            hasStudyVisit,
          })}
        </p>
      </header>

      <div className="member-home__columns">
        <div className="member-home__primary">
          <TodoCard tasks={tasks} />
          {hasStudyVisit ? (
            <StudyVisitCard
              collections={sampleCollections}
              onStartPending={onStartPending}
            />
          ) : null}
        </div>

        <aside className="member-home__aside">
          <section className="member-home-card">
            <h2 className="member-home-card__title member-home-card__title--sm">Questions?</h2>
            <p className="member-home-visit__body">
              Reach the study team with questions about your visit or account.
            </p>
            <p className="member-home-aside-contact">
              <a className="member-home-linkish" href="mailto:members@onyx.bio">
                members@onyx.bio
              </a>
            </p>
          </section>
        </aside>
      </div>

      <section className="member-home-info" data-testid="member-home-info">
        <div className="member-home-info__intro">
          <h2>Your information</h2>
          <p>Everything you&apos;ve shared with us. Open a section to review it.</p>
        </div>

        <div className="member-home-accordion">
          <AccordionItem
            id="consent"
            label="Consent documents"
            meta={consentMeta}
            openId={openId}
            setOpenId={setOpenId}
          >
            {consents.length === 0 ? (
              <p className="member-home-visit__body">
                Consent details will appear here once available.
              </p>
            ) : (
              <ul className="member-home-record-list">
                {consents.map(({ key, label, entry }) => {
                  const tag = consentStatusTag(entry);
                  const signed = consentSignedLabel(entry);
                  return (
                    <li key={key} className="member-home-record">
                      <div className="member-home-record__copy">
                        <p className="member-home-record__title">{label}</p>
                        {signed ? (
                          <p className="member-home-record__sub">{signed}</p>
                        ) : null}
                      </div>
                      <div className="member-home-record__actions">
                        <Tag variant={tag.variant} density="compact">
                          {tag.label}
                        </Tag>
                        {onDownloadConsent && entry.document_id ? (
                          <Button
                            type="button"
                            variant="secondary"
                            size="compact"
                            onClick={() => onDownloadConsent(entry.document_id)}
                          >
                            Download
                          </Button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </AccordionItem>

          {showUpdatesAccordion ? (
            <AccordionItem
              id="updates"
              label={updatesStep.label || 'Health Updates'}
              meta={updatesMeta}
              openId={openId}
              setOpenId={setOpenId}
            >
              <AnswerList data={updatesData} />
              {updatesStep.can_open && updatesStep.path ? (
                <Button
                  type="button"
                  variant="secondary"
                  component={Link}
                  to={updatesStep.path}
                >
                  Update answers
                </Button>
              ) : null}
            </AccordionItem>
          ) : null}

          {otherCompletedOptional.map((step) => (
            <AccordionItem
              key={step.key}
              id={`form-${step.key}`}
              label={step.label || step.key}
              meta="Completed"
              openId={openId}
              setOpenId={setOpenId}
            >
              {step.can_open && step.path ? (
                <Button type="button" variant="secondary" component={Link} to={step.path}>
                  Update answers
                </Button>
              ) : null}
            </AccordionItem>
          ))}

          {fastenEnabled ? (
            <AccordionItem
              id="portals"
              label="Health portals"
              meta={
                fastenConnections.length
                  ? `${fastenConnections.length} connected`
                  : 'Not connected'
              }
              openId={openId}
              setOpenId={setOpenId}
            >
              {fastenConnections.length === 0 ? (
                <p className="member-home-visit__body">
                  No health portals connected yet. Connect one below to share your records.
                </p>
              ) : (
                <dl className="member-home-dl">
                  {fastenConnections.map((conn) => (
                    <div key={conn.id} className="member-home-dl__row">
                      <dt>{conn.provider_name || conn.platform_type || 'Provider'}</dt>
                      <dd>
                        Connected
                        {conn.last_sync_at
                          ? ` · Last synced ${formatDateNoTz(conn.last_sync_at)}`
                          : ''}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
              {fastenWidget}
            </AccordionItem>
          ) : null}

          {preScreenData && Object.keys(preScreenData).length > 0 ? (
            <AccordionItem
              id="prescreen"
              label="Pre-screen responses"
              meta="Completed"
              openId={openId}
              setOpenId={setOpenId}
            >
              <AnswerList data={preScreenData} />
            </AccordionItem>
          ) : null}

          {verificationData ? (
            <AccordionItem
              id="verify"
              label="Identity verification"
              meta="Verified"
              openId={openId}
              setOpenId={setOpenId}
            >
              <dl className="member-home-dl">
                <div className="member-home-dl__row">
                  <dt>Name</dt>
                  <dd>
                    {[
                      formatDisplayName(verificationData.first_name),
                      formatDisplayName(verificationData.last_name),
                    ].filter(Boolean).join(' ')}
                  </dd>
                </div>
                <div className="member-home-dl__row">
                  <dt>Date of birth</dt>
                  <dd>{formatDateNoTz(verificationData.date_of_birth)}</dd>
                </div>
                {verificationStreet ? (
                  <div className="member-home-dl__row">
                    <dt>Address on ID</dt>
                    <dd>
                      {verificationStreet}
                      {verificationStreet2 ? (
                        <>
                          <br />
                          {verificationStreet2}
                        </>
                      ) : null}
                      {verificationCityStateZip ? (
                        <>
                          <br />
                          {verificationCityStateZip}
                        </>
                      ) : null}
                    </dd>
                  </div>
                ) : null}
              </dl>
            </AccordionItem>
          ) : null}
        </div>
      </section>
    </div>
  );
}

export default MemberHome;
